import type Dexie from "dexie";
import type { PrimerDesignParams, ViewerDefaults } from "./db";

export const defaultPrimerParams: PrimerDesignParams = {
  productSizeMin: 100,
  productSizeMax: 1000,
  numReturn: 5,
  primerMinSize: 18,
  primerOptSize: 20,
  primerMaxSize: 27,
  primerMinTm: 57,
  primerOptTm: 60,
  primerMaxTm: 63,
  primerMinGc: 20,
  primerMaxGc: 80,
  primerSaltConc: 50,
  primerDnaConc: 50,
  maxDegeneracy: 1,
};

export const defaultViewerDefaults: ViewerDefaults = {
  sequence: {
    showFeatures: true,
    showRestrictionSites: false,
    showGcContent: false,
    showSequenceGaps: false,
    complementMode: "none",
    translationFrames: [1, 2, 3],
    geneticCodeId: 1,
  },
  msa: {
    visibleTracks: ["consensus"],
  },
  collection: {
    showGcContent: false,
  },
};

export function registerMigrations(db: Dexie) {
  db.version(1).stores({
    objects:
      "++id, content, createdAt, name, parent, size, type, deletedAt, ownerId",
    settings: "id",
    sequences: "++id, name, kind, length, createdAt, ownerId",
    genomes: "++id, name, createdAt, ownerId",
    segments: "++id, genomeId, name, index, length",
    segmentChunks: "id, segmentId, index, [segmentId+index]",
    chromatograms: "id",
    features: "id, sequenceId, type, [sequenceId+type]",
    primers: "id, sequenceId, createdAt, ownerId",
    trackConfigs: "id, sequenceId, trackType, order",
    recents: "&objectId, viewedAt, ownerId",
    alignments: "++id, kind, createdAt, ownerId",
    alignedSequences: "++id, alignmentId",
    sequenceOperations: "++id, sequenceId, [sequenceId+index]",
    sequenceCheckpoints:
      "++id, sequenceId, operationIndex, [sequenceId+operationIndex]",
    collectionOperations: "++id, collectionFileId, [collectionFileId+index]",
    cachedUser: "id",
    blastSearches: "id, rid, status, createdAt, ownerId",
    blastResults: "id, searchId",
    interproSearches: "id, jobId, status, createdAt, ownerId",
    interproResults: "id, searchId",
  });

  db.version(2).stores({
    blastSearches: "id, rid, status, createdAt, ownerId, sequenceId",
    interproSearches: "id, jobId, status, createdAt, ownerId, sequenceId",
  });

  db.version(3).stores({
    sequenceChunks: "[sequenceId+index]",
    genomes: null,
    segments: null,
    segmentChunks: null,
  });

  db.version(4).stores({});

  db.version(5).stores({
    imports: "id, status",
  });

  db.version(6).stores({
    featureUsage: "&featureId, usedAt",
  });

  db.version(7)
    .stores({
      primers: "id, sequenceId, createdAt, ownerId, inLibrary",
    })
    .upgrade((tx) =>
      tx
        .table("primers")
        .toCollection()
        .modify((primer) => {
          if (primer.inLibrary === undefined) {
            primer.inLibrary = true;
          }
        }),
    );

  db.version(8)
    .stores({})
    .upgrade((tx) =>
      tx
        .table("settings")
        .toCollection()
        .modify((settings) => {
          if (settings.autoEmptyTrashDays === undefined) {
            settings.autoEmptyTrashDays = 30;
          }
        }),
    );

  db.version(9)
    .stores({})
    .upgrade((tx) =>
      tx
        .table("settings")
        .toCollection()
        .modify((settings) => {
          if (settings.completionTracking === undefined) {
            settings.completionTracking = true;
          }
        }),
    );

  db.version(10).stores({
    treeContent: "id",
  });

  db.version(11).stores({
    objects:
      "++id, content, createdAt, name, parent, size, type, deletedAt, ownerId, [parent+name]",
  });

  db.version(12)
    .stores({})
    .upgrade((tx) =>
      tx
        .table("treeContent")
        .toCollection()
        .modify((record) => {
          if (record.method === undefined) {
            record.method = "nj";
          }
          if (record.distanceModel === undefined) {
            record.distanceModel = "jc69";
          }
          if (record.gapDeletion === undefined) {
            record.gapDeletion = "pairwise";
          }
        }),
    );

  db.version(13)
    .stores({})
    .upgrade((tx) =>
      tx
        .table("sequences")
        .toCollection()
        .modify((sequence) => {
          if (sequence.genbank) {
            sequence.description = sequence.genbank.definition ?? "";
            sequence.organism = sequence.genbank.organism ?? "";
            sequence.taxonomy = sequence.genbank.taxonomy ?? "";
            sequence.accession = sequence.genbank.accession ?? "";
            sequence.version = sequence.genbank.version ?? "";
            sequence.comment = sequence.genbank.comment ?? "";
            sequence.references = sequence.genbank.references ?? [];
            delete sequence.genbank;
          } else {
            sequence.description = "";
            sequence.organism = "";
            sequence.taxonomy = "";
            sequence.accession = "";
            sequence.version = "";
            sequence.comment = "";
            sequence.references = [];
          }

          if (sequence.customMetadata === undefined) {
            sequence.customMetadata = [];
          }
        }),
    );

  db.version(14)
    .stores({})
    .upgrade((tx) =>
      tx
        .table("settings")
        .toCollection()
        .modify((settings) => {
          const sequenceDefaults = settings.viewerDefaults?.sequence;
          if (
            sequenceDefaults &&
            sequenceDefaults.showSequenceGaps === undefined
          ) {
            sequenceDefaults.showSequenceGaps = false;
          }
        }),
    );
}
